import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './ui/command';
import { Stethoscope, HelpCircle, Video, MapPin, UserPlus, Calendar, Newspaper, GraduationCap, Handshake, Banknote } from 'lucide-react';
import { useSiteSearch } from '../context/SearchContext';
import { useBookingModal } from '../context/BookingContext';
import { servicesData } from '../data/mock';
import { serviceFAQs } from '../data/serviceFAQs';
import { allVideos } from '../data/videos';

/**
 * Site-wide search modal — Cmd+K / Ctrl+K or click the header search icon.
 * Indexes services, FAQs, videos, and quick actions. Uses cmdk's built-in
 * fuzzy filtering (no fuse.js dependency needed).
 */
const SiteSearch = () => {
  const { isOpen, closeSearch } = useSiteSearch();
  const { openBooking } = useBookingModal();
  const navigate = useNavigate();

  // Flatten all FAQs into a single list with their service context
  const allFAQs = useMemo(() => {
    const items = [];
    Object.entries(serviceFAQs).forEach(([slug, faqs]) => {
      const service = servicesData.find((s) => s.slug === slug);
      faqs.forEach((faq, idx) => {
        items.push({
          id: `faq-${slug}-${idx}`,
          serviceSlug: slug,
          serviceTitle: service?.title || slug,
          question: faq.question,
          answer: faq.answer,
        });
      });
    });
    return items;
  }, []);

  const go = (path) => {
    closeSearch();
    navigate(path);
  };

  const goAndScroll = (path, hash) => {
    closeSearch();
    if (window.location.pathname === path) {
      document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(path + hash);
    }
  };

  return (
    <CommandDialog open={isOpen} onOpenChange={(open) => { if (!open) closeSearch(); }}>
      <CommandInput
        placeholder="Search services, FAQs, videos…"
        data-testid="site-search-input"
      />
      <CommandList data-testid="site-search-list">
        <CommandEmpty>No results found. Try a different keyword.</CommandEmpty>

        <CommandGroup heading="Quick Actions">
          <CommandItem
            value="book consultation appointment"
            onSelect={() => { closeSearch(); openBooking(); }}
            data-testid="search-action-book"
          >
            <Calendar size={16} className="mr-2 text-teal-700" />
            Book a Consultation
          </CommandItem>
          <CommandItem
            value="refer patient practitioner"
            onSelect={() => go('/refer')}
            data-testid="search-action-refer"
          >
            <UserPlus size={16} className="mr-2 text-teal-700" />
            Refer a Patient
          </CommandItem>
          <CommandItem
            value="location address waterloo westminster bridge road directions"
            onSelect={() => goAndScroll('/', '#location')}
            data-testid="search-action-location"
          >
            <MapPin size={16} className="mr-2 text-teal-700" />
            Visit the Clinic – Waterloo, London
          </CommandItem>
          <CommandItem
            value="media press podcast features"
            onSelect={() => go('/media')}
            data-testid="search-action-media"
          >
            <Newspaper size={16} className="mr-2 text-teal-700" />
            Media &amp; Press Features
          </CommandItem>
          <CommandItem
            value="courses cpd training clinicians"
            onSelect={() => go('/courses')}
            data-testid="search-action-courses"
          >
            <GraduationCap size={16} className="mr-2 text-teal-700" />
            Courses &amp; CPD Training
          </CommandItem>
          <CommandItem
            value="partners network clinic dentist referral"
            onSelect={() => go('/partners')}
            data-testid="search-action-partners"
          >
            <Handshake size={16} className="mr-2 text-teal-700" />
            Trusted Partner Network
          </CommandItem>
          <CommandItem
            value="fees pricing cost gift block subscription"
            onSelect={() => go('/fees')}
            data-testid="search-action-fees"
          >
            <Banknote size={16} className="mr-2 text-teal-700" />
            Fees &amp; Pricing
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Services">
          {servicesData.map((service) => (
            <CommandItem
              key={service.slug}
              value={`service ${service.title} ${service.description || ''}`}
              onSelect={() => go(`/services/${service.slug}`)}
              data-testid={`search-service-${service.slug}`}
            >
              <Stethoscope size={16} className="mr-2 text-teal-700" />
              <div className="search-item-text">
                <div className="search-item-title">{service.title}</div>
                {service.description ? (
                  <div className="search-item-meta">{service.description.slice(0, 90)}…</div>
                ) : null}
              </div>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Common Questions">
          {allFAQs.slice(0, 40).map((faq) => (
            <CommandItem
              key={faq.id}
              value={`faq ${faq.question} ${faq.answer.slice(0, 200)}`}
              onSelect={() => go(`/services/${faq.serviceSlug}`)}
              data-testid={faq.id}
            >
              <HelpCircle size={16} className="mr-2 text-teal-700 shrink-0" />
              <div className="search-item-text">
                <div className="search-item-title">{faq.question}</div>
                <div className="search-item-meta">{faq.serviceTitle}</div>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Videos">
          {allVideos.map((v) => (
            <CommandItem
              key={v.id}
              value={`video ${v.title} ${v.description}`}
              onSelect={() => go('/videos')}
              data-testid={`search-video-${v.id}`}
            >
              <Video size={16} className="mr-2 text-teal-700 shrink-0" />
              <div className="search-item-text">
                <div className="search-item-title">{v.title}</div>
                <div className="search-item-meta">{v.description.slice(0, 90)}…</div>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default SiteSearch;
